'use strict'

// A live-app sign-in belongs to one account and one application. The cookie
// name and path, and the browser storage keys, all include both so a session
// opened in one application is not sent or shown in another.

const { accountNameFromAppLocation } = require('./app-host')

const LEGACY_USER_COOKIE_NAME = 'st.user'
const LEGACY_LOGGED_IN_USER_KEY = 'superturbo_logged_in_user'
const LEGACY_USER_TOKEN_KEY = 'superturbo_user_token'

const ACCOUNT = /^[A-Za-z0-9][A-Za-z0-9_-]{0,62}$/
const APPLICATION = /^[A-Za-z_][A-Za-z0-9_]*$/

function liveUserScope (account, application) {

  const accountName = String(account || '')
  const applicationName = String(application || '')

  if (!ACCOUNT.test(accountName) || !APPLICATION.test(applicationName)) return null

  return { account: accountName, application: applicationName }
}

function userCookiePath (account, application) {

  const scope = liveUserScope(account, application)

  if (!scope) return ''

  return `/${scope.account}/${scope.application}`
}

function userCookieName (account, application) {

  const scope = liveUserScope(account, application)

  if (!scope) return ''

  return `st.user.${scope.account}.${scope.application}`
}

function loggedInUserStorageKey (account, application) {

  const scope = liveUserScope(account, application)

  if (!scope) return ''

  return `superturbo_logged_in_user:${scope.account}:${scope.application}`
}

function userTokenStorageKey (account, application) {

  const scope = liveUserScope(account, application)

  if (!scope) return ''

  return `superturbo_user_token:${scope.account}:${scope.application}`
}

function forgetLegacyLiveUser (store) {

  if (!store || typeof store.removeItem !== 'function') return

  store.removeItem(LEGACY_LOGGED_IN_USER_KEY)
  store.removeItem(LEGACY_USER_TOKEN_KEY)
}

function rememberedLiveUser (store, account, application) {

  forgetLegacyLiveUser(store)

  const key = loggedInUserStorageKey(account, application)

  if (!store || !key || typeof store.getItem !== 'function') return ''

  return store.getItem(key) || ''
}

function rememberLiveUser (store, account, application, username) {

  forgetLegacyLiveUser(store)

  const key = loggedInUserStorageKey(account, application)

  if (!store || !key || !username || typeof store.setItem !== 'function') return

  store.setItem(key, username)
}

function forgetLiveUser (store, account, application) {

  forgetLegacyLiveUser(store)

  if (!store || typeof store.removeItem !== 'function') return

  const userKey = loggedInUserStorageKey(account, application)
  const tokenKey = userTokenStorageKey(account, application)

  if (userKey) store.removeItem(userKey)
  if (tokenKey) store.removeItem(tokenKey)
}

function liveAppWebSocketUrl (base, account, application) {

  const origin = String(base || '').replace(/\/$/, '')
  const path = userCookiePath(account, application)

  if (!origin || !path) return origin

  return `${origin}${path}`
}

function applicationFromLivePath (pathname) {

  const name = String(pathname || '').split('?')[0].split('/').filter(Boolean)[0] || ''

  if (!liveUserScope('default', name)) return ''

  return name
}

function liveUserScopeFromLocation (location, override) {

  const fromHost = accountNameFromAppLocation(location)

  return {
    account: (override && override.account) || fromHost || 'default',
    application: (override && override.application) || applicationFromLivePath(location && location.pathname)
  }
}

function scopeFromRequestUrl (url, origin) {

  if (!url) return null

  let pathname = ''

  try {

    pathname = new URL(url, origin || 'http://localhost').pathname
  } catch (error) {

    return null
  }

  const scope = requestScopeFromPath(pathname)

  if (!scope) return null

  return {
    account: scope.accountUniqueTitle,
    application: scope.applicationUniqueTitle
  }
}

function sessionProbeResult (status, data) {

  if (status === 401) {

    return {
      signedOut: true,
      username: '',
      expiresAt: null,
      sessionEnded: !!(data && data.sessionEnded),
      reason: data && data.reason || ''
    }
  }

  const username = data && typeof data.username === 'string' ? data.username : ''

  return {
    signedOut: false,
    username,
    expiresAt: data && data.expiresAt,
    sessionEnded: false,
    reason: ''
  }
}

function requestScopeFromPath (path) {

  const parts = String(path || '').split('?')[0].split('/').filter(Boolean)

  if (parts.length < 2) return null

  const scope = liveUserScope(parts[0], parts[1])

  if (!scope) return null

  return {
    accountUniqueTitle: scope.account,
    applicationUniqueTitle: scope.application
  }
}

module.exports = {
  LEGACY_USER_COOKIE_NAME,
  LEGACY_LOGGED_IN_USER_KEY,
  LEGACY_USER_TOKEN_KEY,
  liveUserScope,
  userCookiePath,
  userCookieName,
  loggedInUserStorageKey,
  userTokenStorageKey,
  rememberedLiveUser,
  rememberLiveUser,
  forgetLiveUser,
  liveAppWebSocketUrl,
  liveUserScopeFromLocation,
  scopeFromRequestUrl,
  sessionProbeResult,
  requestScopeFromPath
}
